import { FC, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from '@shared/assets/icons';
import { Tooltip } from '@shared/ui/tooltip';
import { colors } from '@shared/tokens/colors.stylex';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { useScrollContainerDropdownPosition } from '@features/event-participant-card/lib/use-scroll-container-dropdown-position';
import clsx from 'clsx';
import styles from './invitation-status-dropdown.module.scss';

interface InvitationStatusDropdownProps {
  anchorRef: RefObject<HTMLElement | null>;
  isInviteAccepted: boolean;
  isInviteDeclined: boolean;
  isDropdownOpen: boolean;
  onClose: () => void;
  onSelectAccepted: () => void;
  onSelectDeclined: () => void;
  eventDateEnd?: string;
  sendTicketAfterAcceptAt?: string | null;
}

export const InvitationStatusDropdown: FC<InvitationStatusDropdownProps> = ({
  anchorRef,
  isInviteAccepted,
  isInviteDeclined,
  isDropdownOpen,
  onClose,
  onSelectAccepted,
  onSelectDeclined,
  eventDateEnd,
  sendTicketAfterAcceptAt,
}) => {
  const { menuRef, portalTarget, positionStyle, isInline } = useScrollContainerDropdownPosition({
    anchorRef,
    isOpen: isDropdownOpen,
    onClose,
  });

  const isDatePassed = (date?: string | null) => {
    if (!date) return false;
    const checkDate = new Date(date);
    const now = new Date();
    return checkDate <= now;
  };

  const isEventDatePassed = isDatePassed(eventDateEnd);
  const isTicketDatePassed = isDatePassed(sendTicketAfterAcceptAt);
  const isBlocked = isEventDatePassed || isTicketDatePassed;

  const isAcceptedDisabled = isBlocked && !isInviteAccepted;
  const isDeclinedDisabled = isBlocked && !isInviteDeclined;

  const tooltipText = isTicketDatePassed
    ? 'Нельзя изменить, билеты уже отправлены.\nВыполните повторную отправку билетов'
    : 'Нельзя изменить, так как мероприятие прошло';

  const options = [
    {
      key: 'accepted',
      label: 'Принято',
      isDisabled: isAcceptedDisabled,
      isActive: isInviteAccepted,
      onSelect: onSelectAccepted,
    },
    {
      key: 'declined',
      label: 'Не принято',
      isDisabled: isDeclinedDisabled,
      isActive: isInviteDeclined,
      onSelect: onSelectDeclined,
    },
  ];

  if (!isDropdownOpen) {
    return null;
  }

  const menu = (
    <div
      ref={menuRef}
      className={clsx(styles.invitationStatusDropdown, isInline && styles.invitationStatusDropdownInline)}
      style={isInline ? undefined : positionStyle}
    >
      {options.map((option) => (
        <Tooltip key={option.key} position="top" title={tooltipText} disabled={!option.isDisabled}>
          <UnstyledButton onClick={option.isDisabled ? undefined : option.onSelect}>
            <div
              className={clsx(styles.invitationStatusDropdownItem, {
                [styles.invitationStatusDropdownItemDisabled]: option.isDisabled,
              })}
            >
              <span className={styles.invitationStatusDropdownText}>{option.label}</span>
              <CheckIcon width="20" height="20" color={option.isActive ? colors.accentIcon : 'transparent'} />
            </div>
          </UnstyledButton>
        </Tooltip>
      ))}
    </div>
  );

  if (isInline) {
    return menu;
  }

  if (!portalTarget) {
    return null;
  }

  return createPortal(menu, portalTarget);
};
